/**
 * Indodax timeframe configuration and interval mapping.
 * Uses backend API which supports: 1m, 5m, 15m, 1h, 4h, 1d, 1w.
 * @see Backend metrics API /api/v1/balance/chart-klines
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-RestAPI.md#ohlc-history
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Indodax supported kline intervals.
 */
export const INDODAX_TIMEFRAMES: TimeframeConfig = {
  native: ['1', '5', '15', '60', '240', '1D', '1W'],
  synthetic: [], // No synthetic intervals - backend API only
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('indodax', INDODAX_TIMEFRAMES);

/**
 * Maps TradingView resolution to Indodax interval format.
 */
export const mapToIndodaxInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  // Exact matches for all supported intervals
  if (seconds === 60) return '1'; // 1 minute
  if (seconds === 900) return '15'; // 15 minutes
  if (seconds === 1800) return '30'; // 30 minutes
  if (seconds === 3600) return '60'; // 1 hour
  if (seconds === 14400) return '240'; // 4 hours
  if (seconds === 86400) return '1D'; // 1 day
  if (seconds === 259200) return '3D'; // 3 days
  if (seconds === 604800) return '1W'; // 1 week

  // Default to closest supported interval (round down)
  if (seconds < 60) return '1';
  if (seconds < 900) return '1';
  if (seconds < 1800) return '15';
  if (seconds < 3600) return '30';
  if (seconds < 14400) return '60';
  if (seconds < 86400) return '240';
  if (seconds < 259200) return '1D';
  if (seconds < 604800) return '3D';

  return '1W';
};
